import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { API } from 'api';
import { AxiosResponse } from 'axios';
import { Region } from 'shared/types';
import { useAuthState } from 'state/useAuthState';

const ONE_HOUR = 1000 * 60 * 60;

const fetchUserRegions = (): Promise<AxiosResponse<Region[]>> =>
  API.get<Region[]>('regions/authorized');

export type UseUserRegionsQuery = UseQueryResult<
  AxiosResponse<Region[]> | undefined
> & {
  regions: Region[];
};

export const useUserRegionsQuery = (): UseUserRegionsQuery => {
  const {
    permissions: { isInternalUser, canReportWildfire, canCreateLocation },
  } = useAuthState();

  const query = useQuery({
    queryKey: ['userRegions'],
    queryFn: fetchUserRegions,
    staleTime: 48 * ONE_HOUR,
    gcTime: 49 * ONE_HOUR,
    enabled: isInternalUser || canReportWildfire || canCreateLocation,
  });

  const regions = query.data?.data || [];

  // return sorted regions by state, then by displayName for grouping in the autocomplete
  const sortedRegions = regions.sort((a, b) => {
    if (a.state === b.state) {
      return a.displayName > b.displayName ? 1 : -1;
    }
    return a.state > b.state ? 1 : -1;
  });

  return { ...query, regions: sortedRegions };
};
